import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Divider,
  useMediaQuery
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import ApartmentIcon from '@mui/icons-material/Apartment';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import CampaignIcon from '@mui/icons-material/Campaign';
import AppsIcon from '@mui/icons-material/Apps';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Collapse from '@mui/material/Collapse';
import { db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import { Menu, MenuItem } from "@mui/material";
import { Avatar } from "@mui/material";

const drawerWidth = 260;

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isSuperAdmin, isAppAdmin } = useAuth();
  const isMobile = useMediaQuery('(max-width: 900px)');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [noticeMenuOpen, setNoticeMenuOpen] = useState(false);
  const [appsMenuOpen, setAppsMenuOpen] = useState(false);
  const isAdmin = user?.role === 'admin';
  const isHost = user?.role === 'host' || user?.role === 'tenant';
  const isAppAdminRole = user?.role === 'appadmin';
  const isVisitor = user?.role === 'visitor';
  const isOperator = user?.role === 'appoperator';
  const isFacilityAdmin = user?.role === 'facilityadmin';
  const [tenantType, setTenantType] = useState(null);
  const [facilityType, setFacilityType] = useState(null);

  const getPortalTitle = () => {
    if (isSuperAdmin()) return 'Super Admin Portal';
    if (isAppAdminRole) return 'Admin Portal';
    if (isFacilityAdmin) return 'Facility Admin Portal';
    if (isAdmin || location.pathname.startsWith('/admin')) return 'Admin Portal';
    if (isHost || location.pathname.startsWith('/host') || location.pathname.startsWith('/tenant')) {
      return facilityType === 100 ? 'Member Portal' : 'Host Portal';
    }
    if (isOperator || location.pathname.includes('operator')) return 'Operator Portal';
    return 'Visitor Portal';
  };

  useEffect(() => {
    const fetchTypes = async () => {
      if (!user?.tenantId) return;

      try {
        // Get tenant type
        const tenantRef = doc(db, "tenantmaster", user.tenantId);
        const tenantSnap = await getDoc(tenantRef);

        if (tenantSnap.exists()) {
          const tType = tenantSnap.data().tenantType;
          setTenantType(tType);

          // If mixed tenant → load facility type
          if (tType === 300 && user?.facilityId) {

            const facilityRef = doc(
              db,
              "tenantmaster",
              user.tenantId,
              "facilities",
              user.facilityId
            );

            const facilitySnap = await getDoc(facilityRef);

            if (facilitySnap.exists()) {
              setFacilityType(facilitySnap.data().facilityType);
            }
          }

          else {
            setFacilityType(tType);
          }
        }
      } catch (err) {
        console.error("Failed to fetch types", err);
      }
    };
    fetchTypes();
  }, [user]);

  const getNavigationItems = () => {
    if (isSuperAdmin()) {
      return [
        { label: 'Dashboard', path: '/superadmin-dashboard', icon: <DashboardIcon /> },
        { label: 'Org Management', path: '/host-management', icon: <ApartmentIcon /> },
        { label: 'Apps', type: 'appsMenu', icon: <AppsIcon /> }
      ];
    }
    if (isAppAdminRole) {
      return [
        { label: 'Dashboard', path: '/tenant-admin-dashboard', icon: <DashboardIcon /> },
        { label: 'Templates', path: '/templates', icon: <PeopleIcon /> },
        { label: 'Notices', type: 'noticeMenu', icon: <CampaignIcon /> },
        { label: 'Apps', type: 'appsMenu', icon: <AppsIcon /> }
      ];
    }
    if (isFacilityAdmin) {
      return [
        { label: 'Dashboard', path: '/facility-admin-dashboard', icon: <DashboardIcon /> },
        { label: 'Templates', path: '/templates', icon: <PeopleIcon /> },
        { label: 'User Management', path: '/user-management', icon: <ApartmentIcon /> },
        { label: 'Notices', type: 'noticeMenu', icon: <CampaignIcon /> },
        { label: 'Apps', type: 'appsMenu', icon: <AppsIcon /> }
      ];
    }
    if (isAdmin) {
      return [
        { label: 'Dashboard', path: '/host-dashboard', icon: <DashboardIcon /> },
        { label: 'Apps', type: 'appsMenu', icon: <AppsIcon /> }
      ];
    }
    if (isHost) {
      if (facilityType === 100) {
        return [
          { label: 'Dashboard', path: '/host-dashboard', icon: <DashboardIcon /> },
          { label: 'Templates', path: '/templates', icon: <PeopleIcon /> },
          { label: 'Notice Board', path: '/notice-board', icon: <CampaignIcon /> },
          { label: 'Apps', type: 'appsMenu', icon: <AppsIcon /> }
        ];
      }
      return [
        { label: 'Dashboard', path: '/host-dashboard', icon: <DashboardIcon /> },
        { label: 'Templates', path: '/templates', icon: <PeopleIcon /> },
        { label: 'Apps', type: 'appsMenu', icon: <AppsIcon /> }
      ];
    }
    if (isOperator) {
      return [
        { label: 'Dashboard', path: '/app-operator-dashboard', icon: <DashboardIcon /> },
        { label: 'Templates', path: '/templates', icon: <PeopleIcon /> },
        { label: 'Notices', type: 'noticeMenu', icon: <CampaignIcon /> },
        { label: 'Apps', type: 'appsMenu', icon: <AppsIcon /> }
      ];
    }
    if (isVisitor) {
      return [
        { label: 'Dashboard', path: '/visitor-dashboard', icon: <DashboardIcon /> },
        { label: 'Apps', type: 'appsMenu', icon: <AppsIcon /> }
      ];
    }
    return [];
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
      setDrawerOpen(false);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleNavigation = (path) => {
    navigate(path);
    setDrawerOpen(false);
  };

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleProfileClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const getInitial = (email) => {
    if (!email) return "U";
    return email.charAt(0).toUpperCase();
  };

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>

      {/* HEADER */}
      <Typography sx={{
        px: 2,
        py: 2,
        fontWeight: 800,
        fontFamily: "'Unbounded', sans-serif",
        color: "#1c1400"
      }}>
        {getPortalTitle()}
      </Typography>

      <Divider sx={{ bgcolor: "#f0d060" }} />

      {/* NAV ITEMS */}
      <List sx={{ flexGrow: 1 }}>
        {getNavigationItems().map((item, index) => {

          if (item.type === "noticeMenu") {
            return (
              <React.Fragment key={index}>

                <ListItemButton
                  onClick={() => setNoticeMenuOpen(!noticeMenuOpen)}
                  sx={{
                    mx: 1,
                    my: 0.5,
                    borderRadius: "10px",
                    "&:hover": {
                      background: "#fde047",
                      color: "#1c1400"
                    }
                  }}
                >
                  <ListItemIcon sx={{ color: "#1c1400" }}>
                    {item.icon}
                  </ListItemIcon>

                  <ListItemText primary="Notices" />

                  {noticeMenuOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </ListItemButton>

                <Collapse in={noticeMenuOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>

                    <ListItemButton
                      sx={{
                        pl: 4,
                        mx: 1,
                        borderRadius: "8px",
                        "&:hover": {
                          background: "#fde047"
                        }
                      }}
                      onClick={() => handleNavigation('/notice-management')}
                    >
                      <ListItemText primary="Notice Management" />
                    </ListItemButton>

                    <ListItemButton
                      sx={{
                        pl: 4,
                        mx: 1,
                        borderRadius: "8px",
                        "&:hover": {
                          background: "#fde047"
                        }
                      }}
                      onClick={() => handleNavigation('/notice-board')}
                    >
                      <ListItemText primary="Notice Board" />
                    </ListItemButton>

                  </List>
                </Collapse>

              </React.Fragment>
            );
          }
          if (item.type === "appsMenu") {
            return (
              <React.Fragment key={index}>

                <ListItemButton
                  onClick={() => setAppsMenuOpen(!appsMenuOpen)}
                  sx={{
                    mx: 1,
                    my: 0.5,
                    borderRadius: "10px",
                    "&:hover": {
                      background: "#fde047",
                      color: "#1c1400"
                    }
                  }}
                >
                  <ListItemIcon sx={{ color: "#1c1400" }}>
                    {item.icon}
                  </ListItemIcon>

                  <ListItemText primary="Apps" />

                  {appsMenuOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </ListItemButton>

                <Collapse in={appsMenuOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>

                    <ListItemButton
                      sx={{
                        pl: 4,
                        mx: 1,
                        borderRadius: "8px",
                        "&:hover": {
                          background: "#fde047"
                        }
                      }}
                      onClick={() =>
                        window.open(
                          "https://maintain.omnevu.com/login",
                          "_blank",
                          "noopener,noreferrer"
                        )
                      }
                    >
                      <ListItemText primary="Intelligent Operations" />
                    </ListItemButton>

                    <ListItemButton
                      sx={{
                        pl: 4,
                        mx: 1,
                        borderRadius: "8px",
                        "&:hover": {
                          background: "#fde047"
                        }
                      }}
                      onClick={() =>
                        window.open(
                          "https://work2life.com/",
                          "_blank",
                          "noopener,noreferrer"
                        )
                      }
                    >
                      <ListItemText primary="Work2Life" />
                    </ListItemButton>

                    <ListItemButton
                      sx={{
                        pl: 4,
                        mx: 1,
                        borderRadius: "8px",
                        "&:hover": {
                          background: "#fde047"
                        }
                      }}
                      onClick={() =>
                        window.open(
                          "https://rbn.omnevu.com/dashboard",
                          "_blank",
                          "noopener,noreferrer"
                        )
                      }
                    >
                      <ListItemText primary="RBN" />
                    </ListItemButton>

                  </List>
                </Collapse>

              </React.Fragment>
            );
          }
          return (
            <ListItemButton
              key={index}
              onClick={() => handleNavigation(item.path)}
              sx={{
                mx: 1,
                my: 0.5,
                borderRadius: "10px",

                background:
                  location.pathname === item.path
                    ? "#fde047"
                    : "transparent",

                color:
                  location.pathname === item.path
                    ? "#1c1400"
                    : "#444",

                "&:hover": {
                  background: "#fde047",
                  color: "#1c1400"
                }
              }}
            >
              <ListItemIcon sx={{
                color:
                  location.pathname === item.path
                    ? "#1c1400"
                    : "#555"
              }}>
                {item.icon}
              </ListItemIcon>

              <ListItemText primary={item.label} />
            </ListItemButton>
          );
        })}
      </List>

      <Divider sx={{ bgcolor: "#f0d060" }} />

    </Box>
  );

  return (
    <>
      {/* TOP NAVBAR */}
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          background: "#ffffff",
          color: "#1c1400",
          boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
          borderBottom: "1px solid #f0d060"
        }}
      >
        <Toolbar>

          {/* MENU */}
          <IconButton
            edge="start"
            onClick={() => setDrawerOpen(!drawerOpen)}
            sx={{ mr: 2, color: "#1c1400" }}
          >
            <MenuIcon />
          </IconButton>

          {/* TITLE */}
          <Typography sx={{
            flexGrow: 1,
            fontWeight: 800,
            fontFamily: "'Unbounded', sans-serif"
          }}>
            {getPortalTitle()}
          </Typography>

          {/* BRAND NAME */}
          <Typography
            onClick={() => navigate("/")}
            sx={{
              mr: 2,
              cursor: "pointer",
              fontWeight: 800,
              fontSize: "1.1rem",
              letterSpacing: "2px",
              fontFamily: "'Unbounded', sans-serif",
              color: "#1c1400",
              transition: "0.3s",

              "&:hover": {
                color: "#d4a017",
                transform: "scale(1.03)"
              }
            }}
          >
            AALAY
          </Typography>

          {/* AVATAR */}
          <IconButton onClick={handleProfileClick}>
            <Avatar sx={{
              width: 34,
              height: 34,
              bgcolor: "#fde047",
              color: "#1c1400",
              fontWeight: 700
            }}>
              {getInitial(user?.userEmailID)}
            </Avatar>
          </IconButton>

          {/* PROFILE MENU */}
          <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
            <MenuItem onClick={() => { handleNavigation("/profile"); handleClose(); }}>
              <AccountCircleIcon sx={{ mr: 1 }} />
              Profile
            </MenuItem>

            <MenuItem onClick={() => { handleLogout(); handleClose(); }}>
              <LogoutIcon sx={{ mr: 1 }} />
              Logout
            </MenuItem>
          </Menu>

        </Toolbar>
      </AppBar>

      {/* SIDEBAR */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            background: "#ffffff",
            color: "#1c1400",
            boxSizing: 'border-box',
            top: 64,
            height: 'calc(100% - 64px)',
            borderRight: "1px solid #f0d060"
          },
        }}
      >
        {drawerContent}
      </Drawer>

      <Toolbar />
    </>
  );
};

export default Navbar;
